// ============================================================
// VERIX — Sentry SDK init (frontend)
//
// Phase 4 (audit-2026-04-27) — connects @sentry/react to the existing
// errorReporter bridge (registerExternalReporter). The SDK is dynamically
// imported so the ~200 KB bundle is only fetched when VITE_SENTRY_DSN is
// configured. With no DSN, this file is a no-op.
//
// Required ops actions (NOT done by this slave):
//   - npm i @sentry/react @sentry/browser   (~200 KB gzipped)
//   - Set VITE_SENTRY_DSN at Amplify build env
//   - Set VITE_RELEASE_SHA via Amplify build env (commit SHA)
//
// Until those ship, errors stay in the in-memory errorReporter buffer
// (50 entries) accessible via `window.errorTracker.getBuffer()`.
// ============================================================

import { registerExternalReporter } from './errorReporter';
import { hasAnalyticsConsent } from '@/lib/consentState';

let initialised = false;
// Holds the dynamically-imported Sentry SDK once initSentry() loads it, so the
// bug-report widget can emit a linked event without importing the SDK statically.
let sentrySdk = null;

// Session replay is gated behind the user's `analytics` consent (RGPD Art. 6/7).
// Error capture itself runs under legitimate interest (PII redacted in
// beforeSend); replay is more invasive so it only records once analytics
// consent is granted. `hasAnalyticsConsent` vive en `lib/consentState` porque el
// listener del bus de analítica lo necesita también y no puede importar React.

export async function initSentry() {
  if (initialised) return;
  initialised = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // no-op without DSN

  try {
    // Dynamic import keeps the bundle small when Sentry isn't configured.
    // The literal specifier lets Vite/Rollup resolve and bundle @sentry/react
    // as a lazy-loaded chunk (the package is a fixed dependency); .catch keeps
    // init graceful if the chunk ever fails to load.
    const Sentry = await import('@sentry/react').catch(() => null);
    sentrySdk = Sentry;
    if (!Sentry || typeof Sentry.init !== 'function') {
      // SDK not installed — leave a hint in the console for ops.
      // Avoid console.log: this is informational at boot, intentional.
       
      console.warn('[sentry] DSN set but @sentry/react not installed — npm i @sentry/react');
      return;
    }

    // Replay only when the SDK supports it AND analytics consent is granted.
    // maskAllText + blockAllMedia mask PII (inputs, NIF, IBAN, payroll) in the
    // browser before anything leaves the device.
    const replayConsented = hasAnalyticsConsent() && typeof Sentry.replayIntegration === 'function';
    const integrations = replayConsented
      ? [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })]
      : [];

    Sentry.init({
      dsn,
      environment: import.meta.env.MODE || 'unknown',
      release: import.meta.env.VITE_RELEASE_SHA || undefined,
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      integrations,
      // Don't record normal sessions (conserves the free 50 replays/mo quota);
      // capture a replay only around an error, and only with consent.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: replayConsented ? 1.0 : 0,
      beforeSend(event) {
        // Strip cookies, auth headers, and known sensitive payload fields.
        if (event.request?.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['cookie'];
          delete event.request.headers['x-csrf-token'];
        }
        if (event.request?.data && typeof event.request.data === 'object') {
          for (const k of ['password', 'token', 'secret', 'iban', 'nif']) {
            if (k in event.request.data) event.request.data[k] = '[REDACTED]';
          }
        }
        return event;
      },
    });

    // Forward every captureException from the existing buffer to Sentry.
    registerExternalReporter((err, ctx) => {
      Sentry.captureException(err, { extra: ctx });
    });

    // eslint-disable-next-line no-console
    console.info('[sentry] error tracking initialised');
  } catch (e) {
    // Hard guarantee: Sentry init failures must NEVER break the app.
     
    console.warn('[sentry] init failed:', e?.message);
  }
}

/**
 * Emit a Sentry event for a user-submitted bug report and return its event id
 * (so the backend row can link to the Sentry event / on-error replay). No-op
 * returning null when Sentry isn't loaded (no DSN / SDK absent). Never throws.
 */
export function captureUserBugReport(title, context) {
  if (!sentrySdk || typeof sentrySdk.captureMessage !== 'function') return null;
  try {
    return sentrySdk.captureMessage(`Bug report: ${title}`, {
      level: 'info',
      tags: { source: 'bug_report_widget' },
      extra: context || {},
    }) || null;
  } catch {
    return null;
  }
}

/**
 * Emit a product-analytics event to Sentry. Called by the `verix:analytics` bus
 * listener (main.jsx) — no lo llames directo desde componentes: emite con
 * `track()` y el bus se encarga.
 *
 * Gated tras el consentimiento de analítica y NO tras el interés legítimo que
 * cubre el error capture: esto es analítica de producto (RGPD Art. 6.1.a), la
 * misma línea que ya se aplica al replay. Sin consentimiento, no sale nada.
 *
 * OJO con `props`: `beforeSend` redacta cabeceras y `event.request.data`, pero
 * NO toca `extra` — que es justo donde van. Emitir solo códigos y contadores,
 * nunca IBAN, nombres ni ids de cuenta.
 *
 * No-op silencioso si Sentry no está cargado (sin DSN, o el chunk aún no ha
 * resuelto). Nunca lanza: la analítica jamás puede romper al usuario.
 */
export function captureAnalyticsEvent(name, props) {
  if (!sentrySdk || typeof sentrySdk.captureMessage !== 'function') return null;
  if (!hasAnalyticsConsent()) return null;
  try {
    return sentrySdk.captureMessage(name, {
      level: 'info',
      tags: { source: 'analytics', event: name },
      extra: props || {},
    }) || null;
  } catch {
    return null;
  }
}

export default initSentry;
